import React from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import SingleColumnTemplate from '@/components/layouts/SingleColumnTemplate';
import Jumbotron from '@/components/ui/box/Jumbotron';
import Panel from '@/components/ui/box/Panel';
import Paper from '@/components/ui/box/Paper';
import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import useFetch from '@/hooks/common/useFetch';
import useToast from '@/hooks/store/useToast';

import styles from './EmailChangeSentPage.module.scss';

interface EmailChangeTokenError {
  message: string;
}

interface UIProps {
  email: string;
  onResend: () => void;
  onBack: () => void;
  error?: { message: string } | null;
}

export const UI: React.FC<UIProps> = ({ email, onResend, onBack, error }) => (
  <SingleColumnTemplate>
    <div className={styles.wrapper}>
      <Jumbotron>
        <Paper flex flexItemWide>
          <Panel title="メールアドレス設定" strongHeader centerBody>
            <div className={styles.panelContent}>
              <h2 className={styles.mainTitle}>
                送信されたメールを確認してください
              </h2>
              <div className={styles.textSection}>
                <p className={styles.description}>
                  <b>{email}</b>へメールを送信しました。
                  <br />
                  メールに記載されたURLから登録手続きを行ってください。
                </p>
                <div className={styles.annotationSection}>
                  <p className={`${styles.annotation} ${styles.annotation__1}`}>
                    ※ URLの<span>有効期限は10分</span>
                    です。期限内に手続きできなかった場合
                    再度メールアドレスを登録してください。
                  </p>
                  <p className={`${styles.annotation} ${styles.annotation__2}`}>
                    ※ メールが届かない場合は、ドメイン指定受信設定で
                    「@studysapuri.jp」の許可と、URL付きメールの受信許可設定をしてメールを再送信してください。
                  </p>
                </div>
              </div>
              {error && <ErrorList errors={[error.message]} />}
              <div className={styles.buttonSection}>
                <Button
                  className={styles.button}
                  color="primary"
                  size="m"
                  onClick={onResend}
                >
                  メールを再送信
                </Button>
                <Button
                  className={styles.button}
                  color="link"
                  size="m"
                  onClick={onBack}
                >
                  ＜戻る
                </Button>
              </div>
            </div>
          </Panel>
        </Paper>
      </Jumbotron>
    </div>
  </SingleColumnTemplate>
);

const EmailChangeSentPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { fetch, error } = useFetch<unknown, EmailChangeTokenError>(
    'POST',
    '/api/school/parents/email_change_tokens/create_without_additional_auth',
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const email = location.state?.email as string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const authenticated = location.state?.authenticated as boolean | undefined;

  if (!email || !authenticated) {
    navigate('/mypage/profile');
    return null;
  }

  const handleBack = () => {
    navigate('/mypage/profile/email/change', {
      state: { authenticated: true },
    });
  };

  const handleResend = () => {
    void fetch({ body: { email } }).then(({ status }) => {
      if (status >= 200 && status < 300) {
        showToast(`${email}にメールを送信しました`);
      }
    });
  };

  return (
    <UI
      email={email}
      onResend={handleResend}
      onBack={handleBack}
      error={error}
    />
  );
};

export default EmailChangeSentPage;
